import React from "react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";
import { captureError } from "@/lib/sentry";

/**
 * Top-level error boundary. Catches render/lifecycle errors below and shows a
 * branded fallback UI instead of a blank white page. Must remain a class
 * component — React does not yet support functional error boundaries.
 *
 * iter45al — reports to Sentry through `captureError` which is a NO-OP
 * unless REACT_APP_SENTRY_DSN is set. The scrubber (`src/lib/sentry.js`)
 * strips every buyer field before shipping; PII scrub gate verified in
 * `src/lib/__tests__/sentry.test.js` (11 tests, all passing).
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[error-boundary]", error, info?.componentStack);
    // Send to Sentry with allow-listed context only. project_id + run_id
    // are pulled from window.location.search — that's the shallowest,
    // safest way to reference the crashing project without touching state.
    try {
      const params = new URLSearchParams(window.location.search || "");
      captureError(error, {
        project_id: params.get("project") || undefined,
        run_id: params.get("run") || undefined,
        locale: (typeof document !== "undefined" && document.documentElement.lang) || "en",
        componentStack: info?.componentStack,
      });
    } catch { /* never let telemetry break the fallback UI */ }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const errText = this.state.error?.message || String(this.state.error || "");
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "var(--paper, #F5F7F4)",
          color: "var(--body, #33403B)",
          fontFamily: "Inter, sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
        }}
        data-testid="error-boundary-fallback"
      >
        <div
          style={{
            maxWidth: 560,
            width: "100%",
            background: "#fff",
            border: "1px solid var(--hairline, #E2E7E2)",
            borderRadius: 10,
            padding: "2.5rem",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 10,
              background: "#fff8f2",
              border: "1px solid #ffd7c2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 20,
            }}
          >
            <AlertTriangle size={22} color="#F6611C" />
          </div>
          <div
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "0.7rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--muted, #6E7A74)",
              fontWeight: 600,
            }}
          >
            Something broke
          </div>
          <h1
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontSize: "1.75rem",
              margin: "0.5rem 0 0.75rem",
              color: "var(--charcoal, #16211D)",
              letterSpacing: "-0.015em",
              lineHeight: 1.15,
            }}
          >
            This screen hit an unexpected error.
          </h1>
          <p style={{ fontSize: "0.95rem", lineHeight: 1.55, margin: 0, color: "var(--muted, #6E7A74)" }}>
            The rest of the app is still fine — you can reload this page or go back to the
            home screen. If it keeps happening, share the details below with our team.
          </p>

          {errText && (
            <pre
              data-testid="error-boundary-message"
              style={{
                marginTop: 20,
                padding: "0.75rem 1rem",
                background: "var(--paper, #F5F7F4)",
                border: "1px solid var(--hairline, #E2E7E2)",
                borderRadius: 8,
                fontFamily: "JetBrains Mono, ui-monospace, monospace",
                fontSize: "0.72rem",
                color: "var(--charcoal, #16211D)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                maxHeight: 140,
                overflow: "auto",
              }}
            >
              {errText}
            </pre>
          )}

          <div style={{ display: "flex", gap: "0.75rem", marginTop: 24, flexWrap: "wrap" }}>
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                background: "var(--brand-green, #1A6F2B)",
                color: "#fff",
                border: "1px solid var(--brand-green, #1A6F2B)",
                borderRadius: 10,
                padding: "0.7rem 1.3rem",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <RotateCcw size={14} /> Reload this page
            </button>
            <button
              onClick={this.handleHome}
              data-testid="error-boundary-home"
              style={{
                background: "#fff",
                color: "var(--charcoal, #16211D)",
                border: "1px solid var(--hairline, #E2E7E2)",
                borderRadius: 10,
                padding: "0.7rem 1.3rem",
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                fontSize: "0.9rem",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <Home size={14} /> Go home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
